/**
 * PendingInvitationsCard
 *
 * Top-of-dashboard action card promoting partner-invitation rows that
 * are still awaiting the viewer's accept/reject. Same registration rows
 * the EventDetailPage exposes inline (`registration_type='team_partner'`
 * + `partner_status='pending'`), but surfaced up front so a user can
 * resolve invitations the moment they sign in instead of having to
 * navigate into the event detail page.
 *
 * Why this exists: the previous design buried these on the event detail
 * page, so users routinely missed pending invitations and team primaries
 * ended up chasing them outside the app. The card uses the SAME mutation
 * (`useRespondToPartner`) the event detail page uses, so accept/decline
 * behavior is identical across surfaces and React Query invalidations
 * keep both views in sync.
 *
 * Render rules:
 *   - Hidden completely when the user has zero pending invitations.
 *   - Always shown when invitations exist — ignores dashboard
 *     customization because this is an action item, not informational.
 *   - At most `VISIBLE_LIMIT` rows are rendered inline. Anything beyond
 *     is hinted with a "+N more" line; the user can scroll the list
 *     once it expands (rows are vertically stacked).
 *
 * Profile gate: accepting an invitation requires a complete profile (the
 * EventDetailPage enforces this for the same reason — accepting locks
 * the user into the team-roping entry). Declines are NOT gated. When
 * the gate trips we push the user to /profile?returnTo=/dashboard so
 * they return here after saving.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MailQuestion, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useMyPendingInvitations, type PendingInvitation } from '@/api/extensions/useDashboardData';
import { useRespondToPartner } from '@/api/extensions/useRegistrationOps';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useToast } from '@/contexts/ToastContext';
import { formatDateRange } from '@/utils/formatters';
import { isFullProfileComplete } from '@/utils/profileCompleteness';

const VISIBLE_LIMIT = 5;

export function PendingInvitationsCard() {
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const { showSuccess, showError } = useToast();
  const { data, isLoading } = useMyPendingInvitations();
  const respondToPartner = useRespondToPartner();
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  if (isLoading) return null;
  const invitations = data ?? [];
  if (invitations.length === 0) return null;

  const visible = invitations.slice(0, VISIBLE_LIMIT);
  const overflow = invitations.length - visible.length;

  const handleRespond = async (
    invitation: PendingInvitation,
    accept: boolean,
  ) => {
    // Mirror the EventDetailPage profile gate — accepting locks the
    // user into a team-roping entry, which requires a full profile
    // elsewhere too. Declines are never blocked: a user with a half-
    // filled profile must still be able to back out of an accidental
    // invite. returnTo brings them straight back to the dashboard so
    // they can re-attempt accept after saving.
    if (
      accept &&
      !isFullProfileComplete(profile as Record<string, unknown> | null)
    ) {
      showError(
        'Complete your profile first',
        'You need a full profile before you can accept partner invitations.',
      );
      navigate('/profile?returnTo=/dashboard');
      return;
    }
    setActionLoadingId(invitation.registration_id);
    try {
      await respondToPartner.mutateAsync({
        registrationId: invitation.registration_id,
        accept,
      });
      showSuccess(
        accept ? 'Partner request accepted' : 'Partner request declined',
        accept
          ? invitation.partner_covers_payment
            ? "You're locked in — no payment is required from you."
            : 'Pay your entry to confirm your spot.'
          : 'The team primary has been notified.',
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to respond');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Card
      className="border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30"
      role="region"
      aria-label="Pending event invitations awaiting your response"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-200/80 dark:bg-amber-900/50 p-2 motion-safe:animate-pulse">
            <MailQuestion
              className="h-5 w-5 text-amber-700 dark:text-amber-300"
              aria-hidden="true"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-amber-900 dark:text-amber-100">
              {invitations.length === 1
                ? 'You have a pending event invitation'
                : `You have ${invitations.length} pending event invitations`}
            </h3>
            <p className="text-sm text-amber-800/90 dark:text-amber-200/90 mt-0.5 leading-relaxed">
              Approve or decline below to lock in your team-roping seat —
              your partner is waiting on your response.
            </p>
          </div>
        </div>

        <ul className="space-y-2 pl-1" role="list">
          {visible.map((inv) => {
            const isBusy = actionLoadingId === inv.registration_id;
            const eventName = inv.event?.event_name ?? 'Unknown event';
            const rodeoName = inv.rodeo?.name ?? null;
            const dateLine = inv.rodeo
              ? formatDateRange(inv.rodeo.start_date, inv.rodeo.end_date)
              : '';
            return (
              <li
                key={inv.registration_id}
                className="rounded-md border border-amber-200/70 dark:border-amber-900/40 bg-white/70 dark:bg-amber-950/20 px-3 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">
                      {eventName}
                    </p>
                    {inv.position && (
                      <Badge
                        variant="outline"
                        className="border-amber-400/60 bg-amber-100/70 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100 shrink-0"
                      >
                        {inv.position}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {[
                      rodeoName,
                      dateLine || null,
                      inv.inviter_name
                        ? `invited by ${inv.inviter_name}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {/* Payment-posture hint — same logic the EventDetailPage
                      uses to tell partners whether they'll owe a share
                      after accepting (Pay Later from the primary) vs
                      pay their own (default). Helps the user make a
                      decision without leaving the dashboard. */}
                  <p
                    className={
                      'text-xs flex items-center gap-1 ' +
                      (inv.partner_covers_payment
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-amber-800/90 dark:text-amber-200/90')
                    }
                  >
                    <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                    {inv.partner_covers_payment
                      ? 'Your partner is covering this entry — accepting locks in a free seat.'
                      : "Accepting moves you to Pending Payment so you can settle your share."}
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    onClick={() => handleRespond(inv, false)}
                    disabled={isBusy}
                    aria-label={`Decline invitation to ${eventName}`}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-amber-700 hover:bg-amber-800 text-white"
                    onClick={() => handleRespond(inv, true)}
                    disabled={isBusy}
                    aria-label={`Approve invitation to ${eventName}`}
                  >
                    {isBusy ? 'Approving…' : 'Approve'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>

        {overflow > 0 && (
          <p className="text-xs text-muted-foreground pl-1">
            And {overflow} more — scroll the list or open the event detail
            page to act on the rest.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
